'use client'

/**
 * TelemetryTracker — client-side telemetry instrumentation.
 *
 * Tracks page-level activity only (no form values, keystrokes, or secrets):
 *   - Session start (on mount)
 *   - Page enter/leave (on route change)
 *   - Heartbeat every 60s while page is visible and active
 *   - Session end (on unload/visibilitychange)
 *
 * Uses navigator.sendBeacon for reliable unload telemetry.
 * Batches events and debounces to minimize write amplification.
 */

import { useEffect, useRef, useCallback } from 'react'
import { usePathname } from 'next/navigation'

interface TelemetryEvent {
  type: 'session_start' | 'page_enter' | 'page_leave' | 'heartbeat' | 'session_end'
  sessionId?: string
  pathname?: string
  routeKey?: string
  visitId?: string
  userAgent?: string
  viewport?: string
  screen?: string
  dpr?: number
  diagnostics?: ClientDiagnostics
  timestamp?: string
  endReason?: string
  /** Unload events only: the server credits the last stretch only if the tab was being looked at. */
  visible?: boolean
}

/** Best-effort network + hardware + load-timing signals for slowness triage. */
interface ClientDiagnostics {
  deviceMemory?: number
  cpuCores?: number
  connType?: string
  downlinkMbps?: number
  rttMs?: number
  loadMs?: number
  ttfbMs?: number
}

const HEARTBEAT_INTERVAL = 60_000 // 60 seconds
const FLUSH_DEBOUNCE = 2_000      // 2 second debounce for batch flush
const INGEST_URL = '/api/telemetry/ingest'

function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Capture the current device resolution (always-on, page-level only):
 *   viewport — the browser window the page actually sees
 *   screen   — the physical screen resolution
 *   dpr      — devicePixelRatio (pixel density)
 * Re-read on every page load so resizes / external-monitor switches show up.
 */
function getDeviceMetrics(): { viewport?: string; screen?: string; dpr?: number } {
  if (typeof window === 'undefined') return {}
  const vp = `${window.innerWidth}x${window.innerHeight}`
  const scr = typeof screen !== 'undefined' && screen.width ? `${screen.width}x${screen.height}` : undefined
  const dpr = typeof window.devicePixelRatio === 'number'
    ? Math.round(window.devicePixelRatio * 100) / 100
    : undefined
  return { viewport: vp, screen: scr, dpr }
}

/**
 * Capture always-on client diagnostics for "the portal is slow" triage:
 *   - hardware: navigator.deviceMemory (GB) + hardwareConcurrency (cores)
 *   - network: navigator.connection effectiveType / downlink / rtt
 *   - load timing: Navigation Timing loadEventEnd + responseStart (TTFB)
 * All best-effort — a field is omitted when the browser doesn't expose it
 * (e.g. Safari/iOS has no navigator.connection). `includeNav` is true only for
 * the initial hard load (session_start); on client-side route changes the
 * Navigation Timing entry is stale, so page_enter captures the live connection
 * only. Never captures IPs, form values, or keystrokes.
 */
function getClientDiagnostics(includeNav: boolean): { diagnostics?: ClientDiagnostics } {
  if (typeof navigator === 'undefined') return {}
  const d: ClientDiagnostics = {}
  const nav = navigator as Navigator & {
    deviceMemory?: number
    connection?: { effectiveType?: string; downlink?: number; rtt?: number }
  }
  if (typeof nav.deviceMemory === 'number') d.deviceMemory = nav.deviceMemory
  if (typeof nav.hardwareConcurrency === 'number') d.cpuCores = nav.hardwareConcurrency
  const c = nav.connection
  if (c) {
    if (c.effectiveType) d.connType = c.effectiveType
    if (typeof c.downlink === 'number') d.downlinkMbps = c.downlink
    if (typeof c.rtt === 'number') d.rttMs = c.rtt
  }
  if (includeNav && typeof performance !== 'undefined' && performance.getEntriesByType) {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (navEntry) {
      if (navEntry.loadEventEnd > 0) d.loadMs = Math.round(navEntry.loadEventEnd)
      if (navEntry.responseStart > 0) d.ttfbMs = Math.round(navEntry.responseStart)
    }
  }
  return Object.keys(d).length ? { diagnostics: d } : {}
}

function normalizeRouteKey(pathname: string): string {
  return pathname.replace(/^\/internal/, '').replace(/\/$/, '') || '/'
}

export default function TelemetryTracker() {
  const pathname = usePathname()
  const sessionIdRef = useRef<string | null>(null)
  const currentVisitIdRef = useRef<string | null>(null)
  const eventQueueRef = useRef<TelemetryEvent[]>([])
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const sessionStartedRef = useRef(false)
  const lastPathnameRef = useRef<string | null>(null)

  // ── Flush events to server ──────────────────────────────────────────

  const flushEvents = useCallback((useBeacon = false) => {
    const events = eventQueueRef.current
    if (events.length === 0) return

    eventQueueRef.current = []

    const payload = JSON.stringify({ events })

    if (useBeacon && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(INGEST_URL, new Blob([payload], { type: 'application/json' }))
    } else {
      fetch(INGEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {
        // Silently fail — telemetry is best-effort
      })
    }
  }, [])

  const enqueueEvent = useCallback((event: TelemetryEvent) => {
    event.timestamp = new Date().toISOString()
    eventQueueRef.current.push(event)

    // Debounced flush
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
    flushTimerRef.current = setTimeout(() => flushEvents(), FLUSH_DEBOUNCE)
  }, [flushEvents])

  // ── Session lifecycle ───────────────────────────────────────────────

  const startSession = useCallback(() => {
    if (sessionStartedRef.current) return
    const sessionId = generateId('ses')
    sessionIdRef.current = sessionId
    sessionStartedRef.current = true

    enqueueEvent({
      type: 'session_start',
      sessionId,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...getDeviceMetrics(),
      ...getClientDiagnostics(true),
    })

    // Flush session start immediately
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
    flushTimerRef.current = setTimeout(() => flushEvents(), 500)
  }, [enqueueEvent, flushEvents])

  const endSession = useCallback((reason: string) => {
    if (!sessionIdRef.current) return
    const visible = typeof document !== 'undefined' && document.visibilityState === 'visible'

    // Close current visit first
    if (currentVisitIdRef.current) {
      enqueueEvent({
        type: 'page_leave',
        sessionId: sessionIdRef.current,
        visitId: currentVisitIdRef.current,
        endReason: 'unload',
        visible,
      })
      currentVisitIdRef.current = null
    }

    enqueueEvent({
      type: 'session_end',
      sessionId: sessionIdRef.current,
      endReason: reason,
      visible,
    })

    // Flush immediately with beacon
    flushEvents(true)
  }, [enqueueEvent, flushEvents])

  // ── Page visit lifecycle ────────────────────────────────────────────

  const enterPage = useCallback((path: string) => {
    if (!sessionIdRef.current) return

    // Close previous visit
    if (currentVisitIdRef.current) {
      enqueueEvent({
        type: 'page_leave',
        sessionId: sessionIdRef.current,
        visitId: currentVisitIdRef.current,
        endReason: 'route_change',
      })
    }

    const visitId = generateId('vis')
    currentVisitIdRef.current = visitId

    enqueueEvent({
      type: 'page_enter',
      sessionId: sessionIdRef.current,
      visitId,
      pathname: path,
      routeKey: normalizeRouteKey(path),
      ...getDeviceMetrics(),
      ...getClientDiagnostics(false),
    })
  }, [enqueueEvent])

  // ── Heartbeat ───────────────────────────────────────────────────────

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current)
    heartbeatTimerRef.current = setInterval(() => {
      if (!sessionIdRef.current) return
      // Only send heartbeat if page is visible
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return

      enqueueEvent({
        type: 'heartbeat',
        sessionId: sessionIdRef.current,
      })
    }, HEARTBEAT_INTERVAL)
  }, [enqueueEvent])

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current)
      heartbeatTimerRef.current = null
    }
  }, [])

  // ── Initialize session on mount ─────────────────────────────────────

  useEffect(() => {
    startSession()
    startHeartbeat()

    // Visibility change: pause/resume heartbeat
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopHeartbeat()
        // Send a heartbeat to mark last activity
        if (sessionIdRef.current) {
          enqueueEvent({ type: 'heartbeat', sessionId: sessionIdRef.current })
          flushEvents(true)
        }
      } else {
        startHeartbeat()
        // Send heartbeat on return
        if (sessionIdRef.current) {
          enqueueEvent({ type: 'heartbeat', sessionId: sessionIdRef.current })
        }
      }
    }

    // Unload: end session
    const handleBeforeUnload = () => {
      endSession('browser_close_inferred')
    }

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      stopHeartbeat()
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Track route changes ─────────────────────────────────────────────

  useEffect(() => {
    if (!pathname) return
    // Skip if same pathname (e.g. on mount when session not yet started)
    if (pathname === lastPathnameRef.current) return
    lastPathnameRef.current = pathname

    // Ensure session is started before tracking pages
    if (!sessionStartedRef.current) {
      // Wait for session start to flush, then enter page
      const timer = setTimeout(() => enterPage(pathname), 600)
      return () => clearTimeout(timer)
    }

    enterPage(pathname)
  }, [pathname, enterPage])

  // No visible UI
  return null
}
